import {
  getByIds as getDirectoriesByLocalIds,
  getUndeleted as getAllUndeletedDirectories,
} from 'serviceWorker/db/directories/directories';
import { getUndeleted as getAllUndeletedDocumentations } from 'serviceWorker/db/documentations/documentations';
import db from 'serviceWorker/db';

export interface DirectoryContents {
  documentationLocalIds: number[];
  directoryLocalIds: number[];
}

function getDirectoryTraversalKeys(directory: {
  _id?: string;
  localId: number;
}): string[] {
  const keys = new Set<string>();

  if (directory._id) {
    keys.add(directory._id);
  }

  keys.add(String(directory.localId));

  return Array.from(keys);
}

/**
 * Collects undeleted content inside directories (for archiving).
 */
export async function collectDirectoryContents(
  directoryLocalIds: number[]
): Promise<DirectoryContents> {
  if (directoryLocalIds.length === 0) {
    return { documentationLocalIds: [], directoryLocalIds: [] };
  }

  const startingDirectories = await getDirectoriesByLocalIds(directoryLocalIds);
  const startingDirectoryKeys = new Set<string>();
  startingDirectories.forEach((directory) => {
    getDirectoryTraversalKeys(directory).forEach((key) => {
      startingDirectoryKeys.add(key);
    });
  });

  if (startingDirectoryKeys.size === 0) {
    return { documentationLocalIds: [], directoryLocalIds: [] };
  }

  // Fetch all undeleted items in single queries
  const [allDirectories, allDocumentations] = await Promise.all([
    getAllUndeletedDirectories(),
    getAllUndeletedDocumentations(),
  ]);

  // Build parent->children maps
  const directoriesByParentId = new Map<string, typeof allDirectories>();
  for (const dir of allDirectories) {
    if (!dir.parentId) continue;
    const existing = directoriesByParentId.get(dir.parentId) ?? [];
    existing.push(dir);
    directoriesByParentId.set(dir.parentId, existing);
  }

  const documentationsByParentId = new Map<string, typeof allDocumentations>();
  for (const doc of allDocumentations) {
    if (!doc.parentId) continue;
    const existing = documentationsByParentId.get(doc.parentId) ?? [];
    existing.push(doc);
    documentationsByParentId.set(doc.parentId, existing);
  }

  // Collect nested content in memory
  const collectedDirectoryLocalIds: number[] = [];
  const collectedDocumentationLocalIds: number[] = [];
  const processedIds = new Set<string>();
  const toProcess = [...startingDirectoryKeys];

  while (toProcess.length > 0) {
    const currentId = toProcess.pop()!;
    if (processedIds.has(currentId)) continue;
    processedIds.add(currentId);

    const childDirs = directoriesByParentId.get(currentId) ?? [];
    for (const dir of childDirs) {
      collectedDirectoryLocalIds.push(dir.localId);
      getDirectoryTraversalKeys(dir).forEach((key) => {
        toProcess.push(key);
      });
    }

    const childDocs = documentationsByParentId.get(currentId) ?? [];
    for (const doc of childDocs) {
      collectedDocumentationLocalIds.push(doc.localId);
    }
  }

  return {
    documentationLocalIds: Array.from(new Set(collectedDocumentationLocalIds)),
    directoryLocalIds: Array.from(new Set(collectedDirectoryLocalIds)),
  };
}

/**
 * Collects deleted content inside directories (for restoring).
 * Uses single batch queries instead of recursive fetches.
 */
export async function collectDeletedDirectoryContents(
  directoryLocalIds: number[]
): Promise<DirectoryContents> {
  if (directoryLocalIds.length === 0) {
    return { documentationLocalIds: [], directoryLocalIds: [] };
  }

  const startingDirectories = await getDirectoriesByLocalIds(directoryLocalIds);
  const startingDirectoryKeys = new Set<string>();
  startingDirectories.forEach((directory) => {
    getDirectoryTraversalKeys(directory).forEach((key) => {
      startingDirectoryKeys.add(key);
    });
  });

  if (startingDirectoryKeys.size === 0) {
    return { documentationLocalIds: [], directoryLocalIds: [] };
  }

  // Fetch all deleted items in single queries
  const [allDirectories, allDocumentations] = await Promise.all([
    db.directories.where('deleted').equals(1).toArray(),
    db.documentations.where('deleted').equals(1).toArray(),
  ]);

  // Build parent->children maps
  const directoriesByParentId = new Map<string, typeof allDirectories>();
  for (const dir of allDirectories) {
    if (!dir.parentId) continue;
    const existing = directoriesByParentId.get(dir.parentId) ?? [];
    existing.push(dir);
    directoriesByParentId.set(dir.parentId, existing);
  }

  const documentationsByParentId = new Map<string, typeof allDocumentations>();
  for (const doc of allDocumentations) {
    if (!doc.parentId) continue;
    const existing = documentationsByParentId.get(doc.parentId) ?? [];
    existing.push(doc);
    documentationsByParentId.set(doc.parentId, existing);
  }

  // Collect nested content in memory
  const collectedDirectoryLocalIds: number[] = [];
  const collectedDocumentationLocalIds: number[] = [];
  const processedIds = new Set<string>();
  const toProcess = [...startingDirectoryKeys];

  while (toProcess.length > 0) {
    const currentId = toProcess.pop()!;
    if (processedIds.has(currentId)) continue;
    processedIds.add(currentId);

    const childDirs = directoriesByParentId.get(currentId) ?? [];
    for (const dir of childDirs) {
      collectedDirectoryLocalIds.push(dir.localId);
      getDirectoryTraversalKeys(dir).forEach((key) => {
        toProcess.push(key);
      });
    }

    const childDocs = documentationsByParentId.get(currentId) ?? [];
    for (const doc of childDocs) {
      collectedDocumentationLocalIds.push(doc.localId);
    }
  }

  return {
    documentationLocalIds: Array.from(new Set(collectedDocumentationLocalIds)),
    directoryLocalIds: Array.from(new Set(collectedDirectoryLocalIds)),
  };
}

export interface PerFolderContents {
  [directoryLocalId: number]: number;
}

/**
 * Collects nested content count per directory (for archiving).
 * Returns a map of directoryLocalId -> nested items count (dirs + docs).
 */
export async function collectDirectoryContentsPerFolder(
  directoryLocalIds: number[]
): Promise<PerFolderContents> {
  if (directoryLocalIds.length === 0) {
    return {};
  }

  const startingDirectories = await getDirectoriesByLocalIds(directoryLocalIds);
  const localIdToTraversalKeys = new Map<number, string[]>();
  for (const dir of startingDirectories) {
    const keys = getDirectoryTraversalKeys(dir);
    if (keys.length > 0) {
      localIdToTraversalKeys.set(dir.localId, keys);
    }
  }

  if (localIdToTraversalKeys.size === 0) {
    return {};
  }

  const [allDirectories, allDocumentations] = await Promise.all([
    getAllUndeletedDirectories(),
    getAllUndeletedDocumentations(),
  ]);

  const directoriesByParentId = new Map<string, typeof allDirectories>();
  for (const dir of allDirectories) {
    if (!dir.parentId) continue;
    const existing = directoriesByParentId.get(dir.parentId) ?? [];
    existing.push(dir);
    directoriesByParentId.set(dir.parentId, existing);
  }

  const documentationsByParentId = new Map<string, typeof allDocumentations>();
  for (const doc of allDocumentations) {
    if (!doc.parentId) continue;
    const existing = documentationsByParentId.get(doc.parentId) ?? [];
    existing.push(doc);
    documentationsByParentId.set(doc.parentId, existing);
  }

  const result: PerFolderContents = {};

  for (const [localId, startingKeys] of localIdToTraversalKeys) {
    let count = 0;
    const processedIds = new Set<string>();
    const toProcess = [...startingKeys];

    while (toProcess.length > 0) {
      const currentId = toProcess.pop()!;
      if (processedIds.has(currentId)) continue;
      processedIds.add(currentId);

      const childDirs = directoriesByParentId.get(currentId) ?? [];
      for (const dir of childDirs) {
        count++;
        getDirectoryTraversalKeys(dir).forEach((key) => {
          toProcess.push(key);
        });
      }

      const childDocs = documentationsByParentId.get(currentId) ?? [];
      count += childDocs.length;
    }

    result[localId] = count;
  }

  return result;
}

/**
 * Collects deleted nested content count per directory (for restoring).
 * Returns a map of directoryLocalId -> nested items count (dirs + docs).
 */
export async function collectDeletedDirectoryContentsPerFolder(
  directoryLocalIds: number[]
): Promise<PerFolderContents> {
  if (directoryLocalIds.length === 0) {
    return {};
  }

  const startingDirectories = await getDirectoriesByLocalIds(directoryLocalIds);
  const localIdToTraversalKeys = new Map<number, string[]>();
  for (const dir of startingDirectories) {
    const keys = getDirectoryTraversalKeys(dir);
    if (keys.length > 0) {
      localIdToTraversalKeys.set(dir.localId, keys);
    }
  }

  if (localIdToTraversalKeys.size === 0) {
    return {};
  }

  const [allDirectories, allDocumentations] = await Promise.all([
    db.directories.where('deleted').equals(1).toArray(),
    db.documentations.where('deleted').equals(1).toArray(),
  ]);

  const directoriesByParentId = new Map<string, typeof allDirectories>();
  for (const dir of allDirectories) {
    if (!dir.parentId) continue;
    const existing = directoriesByParentId.get(dir.parentId) ?? [];
    existing.push(dir);
    directoriesByParentId.set(dir.parentId, existing);
  }

  const documentationsByParentId = new Map<string, typeof allDocumentations>();
  for (const doc of allDocumentations) {
    if (!doc.parentId) continue;
    const existing = documentationsByParentId.get(doc.parentId) ?? [];
    existing.push(doc);
    documentationsByParentId.set(doc.parentId, existing);
  }

  const result: PerFolderContents = {};

  for (const [localId, startingKeys] of localIdToTraversalKeys) {
    let count = 0;
    const processedIds = new Set<string>();
    const toProcess = [...startingKeys];

    while (toProcess.length > 0) {
      const currentId = toProcess.pop()!;
      if (processedIds.has(currentId)) continue;
      processedIds.add(currentId);

      const childDirs = directoriesByParentId.get(currentId) ?? [];
      for (const dir of childDirs) {
        count++;
        getDirectoryTraversalKeys(dir).forEach((key) => {
          toProcess.push(key);
        });
      }

      const childDocs = documentationsByParentId.get(currentId) ?? [];
      count += childDocs.length;
    }

    result[localId] = count;
  }

  return result;
}
